import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

interface AuditEvent {
  id: string;
  timestampUtc: string;
  user: string;
  role: string;
  mode: string;
  category: string;
  action: string;
  target: string;
  success: boolean;
  dryRun: boolean;
  details: Record<string, string>;
  traceId: string;
}

const CATEGORIES = ['all', 'Auth', 'Tags', 'Network', 'Vm', 'Scheduler', 'Deployment'] as const;

const CATEGORY_COLORS: Record<string, { bg: string; fg: string; border: string }> = {
  Auth:       { bg: '#6f42c122', fg: '#c29fff', border: '#6f42c144' },
  Tags:       { bg: '#007bff22', fg: '#4da6ff', border: '#007bff44' },
  Network:    { bg: '#dc354522', fg: '#ff8f9a', border: '#dc354544' },
  Vm:         { bg: '#28a74522', fg: '#7ee2a8', border: '#28a74544' },
  Scheduler:  { bg: '#fd7e1422', fg: '#ffc188', border: '#fd7e1444' },
  Deployment: { bg: '#17a2b822', fg: '#7fd8e8', border: '#17a2b844' },
};

const AuditLogView: React.FC = () => {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = category === 'all' ? {} : { category };
      const res = await axios.get(`${API_URL}/audit`, { params });
      setEvents(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => { load(); }, [load]);

  const chipStyle = (cat: string) => {
    const c = CATEGORY_COLORS[cat] || { bg: '#25252a', fg: '#b0b0b0', border: '#444' };
    return {
      display: 'inline-block', padding: '0.2rem 0.65rem', borderRadius: '10px',
      fontSize: '0.75rem', fontWeight: 600, flexShrink: 0,
      background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
    } as React.CSSProperties;
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        {CATEGORIES.map(c => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            style={{ padding: '0.5rem 1rem', background: category === c ? '#007bff' : '#25252a', color: category === c ? 'white' : '#b0b0b0', border: '1px solid ' + (category === c ? '#007bff' : '#444'), borderRadius: '6px', cursor: 'pointer' }}
          >
            {c === 'all' ? 'All Categories' : c}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button
          onClick={load}
          style={{ padding: '0.5rem 1rem', background: '#25252a', color: '#b0b0b0', border: '1px solid #444', borderRadius: '6px', cursor: 'pointer' }}
        >
          {loading ? 'Loading...' : '↻ Refresh'}
        </button>
      </div>

      {events.length === 0 ? (
        <div style={{ background: '#25252a', border: '1px solid #444', borderRadius: '8px', padding: '3rem', textAlign: 'center', color: '#888' }}>
          {loading ? 'Loading audit trail...' : 'No audit events recorded yet. Actions performed in the app will appear here.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {events.map(evt => (
            <div key={evt.id} style={{ background: '#25252a', border: '1px solid #444', borderRadius: '8px', overflow: 'hidden' }}>
              <div
                onClick={() => setExpanded(expanded === evt.id ? null : evt.id)}
                style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.85rem 1.25rem', cursor: 'pointer' }}
              >
                <span style={chipStyle(evt.category)}>{evt.category}</span>
                <span style={{ color: '#f0f0f0', fontWeight: 600, fontSize: '0.875rem', flexShrink: 0 }}>{evt.action}</span>
                {evt.dryRun && (
                  <span style={{ background: '#ffc10722', color: '#ffe08a', border: '1px solid #ffc10744', borderRadius: '10px', padding: '0.2rem 0.5rem', fontSize: '0.72rem', flexShrink: 0 }}>DRY RUN</span>
                )}
                {!evt.success && (
                  <span style={{ background: '#dc354522', color: '#ff8f9a', border: '1px solid #dc354544', borderRadius: '10px', padding: '0.2rem 0.5rem', fontSize: '0.72rem', flexShrink: 0 }}>FAILED</span>
                )}
                <span style={{ color: '#b0b0b0', flex: 1, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {evt.target}
                </span>
                <span style={{ color: '#888', fontSize: '0.8rem', flexShrink: 0 }}>
                  {evt.user}{evt.role ? ` (${evt.role})` : ''}
                </span>
                <span style={{ color: '#666', fontSize: '0.8rem', flexShrink: 0 }}>{new Date(evt.timestampUtc).toLocaleString()}</span>
                <span style={{ color: '#666' }}>{expanded === evt.id ? '▲' : '▼'}</span>
              </div>

              {expanded === evt.id && (
                <div style={{ borderTop: '1px solid #333', padding: '1rem 1.25rem', background: '#1a1a1e', fontSize: '0.85rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '0.35rem 1rem' }}>
                    <span style={{ color: '#888' }}>Target</span>
                    <span style={{ color: '#e0e0e0', wordBreak: 'break-all' }}>{evt.target || '—'}</span>
                    <span style={{ color: '#888' }}>Mode</span>
                    <span style={{ color: '#e0e0e0' }}>{evt.mode || '—'}</span>
                    <span style={{ color: '#888' }}>Trace ID</span>
                    <span style={{ color: '#e0e0e0', fontFamily: 'monospace' }}>{evt.traceId || '—'}</span>
                    {Object.entries(evt.details || {}).map(([k, v]) => (
                      <React.Fragment key={k}>
                        <span style={{ color: '#888' }}>{k}</span>
                        <span style={{ color: '#e0e0e0', wordBreak: 'break-word' }}>{v}</span>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuditLogView;
